import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type ArtifactType = 'mvp_scope' | 'monetization';

export interface IdeaArtifact {
  id: string;
  idea_id: string;
  user_id: string;
  type: ArtifactType;
  content: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export function useIdeaArtifacts(ideaId: string | null) {
  const { user } = useAuth();
  const [artifacts, setArtifacts] = useState<IdeaArtifact[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchArtifacts = useCallback(async () => {
    if (!user || !ideaId) {
      setArtifacts([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('idea_artifacts' as any)
      .select('*')
      .eq('idea_id', ideaId)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error fetching artifacts:', error);
    } else {
      setArtifacts((data as unknown as IdeaArtifact[]) || []);
    }
    setLoading(false);
  }, [user, ideaId]);

  useEffect(() => {
    fetchArtifacts();
  }, [fetchArtifacts]);

  const getArtifact = (type: ArtifactType): IdeaArtifact | undefined =>
    artifacts.find(a => a.type === type);

  const upsertArtifact = async (
    type: ArtifactType,
    content: Record<string, unknown>
  ): Promise<IdeaArtifact | null> => {
    if (!user || !ideaId) return null;

    const existing = getArtifact(type);

    if (existing) {
      const { data, error } = await supabase
        .from('idea_artifacts' as any)
        .update({ content, updated_at: new Date().toISOString() } as any)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating artifact:', error);
        return null;
      }
      const updated = data as unknown as IdeaArtifact;
      setArtifacts(prev => prev.map(a => a.id === updated.id ? updated : a));
      return updated;
    } else {
      const { data, error } = await supabase
        .from('idea_artifacts' as any)
        .insert({
          idea_id: ideaId,
          user_id: user.id,
          type,
          content,
        } as any)
        .select()
        .single();

      if (error) {
        console.error('Error creating artifact:', error);
        return null;
      }
      const created = data as unknown as IdeaArtifact;
      setArtifacts(prev => [...prev, created]);
      return created;
    }
  };

  return { artifacts, loading, getArtifact, upsertArtifact, refetch: fetchArtifacts };
}
